import "./receptionTypes.js";
import React, { memo } from "react";
import { alpha, Box, useTheme } from "@mui/material";
import { status } from "shared/src/appointment.mjs";
import { ClientName } from "./ClientName";
import * as f from "../../utils/formatter";
import {
  adhocWaitingTimesIcons,
  createClickHandler,
  rawWaitingTimes,
  waitingTimesIcons,
} from "../calendar";
import { createUrlString } from "../../url";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkHorizontal } from "@fortawesome/pro-solid-svg-icons";

export const CheckedInClient = memo(
  /**
   * A component to display a checked in client on the reception dashboard
   *
   * @typedef {object} CheckedInClientProps
   * @property {Appointment} appointment
   * @property {Appointment} parentAppointment
   * @property {string} hoverOverId
   * @property {function} setHoverOverId Setter function for the hoverOverId state
   * @property {Set<string>} nameDayNames
   *
   * @param {CheckedInClientProps} props
   * @returns {ReactElement}
   */
  ({
    appointment,
    parentAppointment,
    hoverOverId,
    setHoverOverId,
    nameDayNames,
    setShowDetailModal,
  }) => {
    const theme = useTheme();

    const handleMouseEnter = (id) => {
      if (id) {
        setHoverOverId(id);
      }
    };

    const handleMouseLeave = () => {
      setHoverOverId("");
    };

    const clickHandler = createClickHandler({
      onClick: () => {
        setShowDetailModal(appointment);
      },
      onDoubleClick: () => {
        const clientId = appointment.client?.id;
        if (clientId == null) {
          return;
        }

        window.open(
          createUrlString(
            location.origin,
            location.search,
            `/clients/${clientId}`,
          ),
          "_blank",
        );
      },
    });

    const waitingTimes = rawWaitingTimes(appointment, new Date());

    const timesIcons = appointment.adhoc_type
      ? adhocWaitingTimesIcons(waitingTimes)
      : waitingTimesIcons(waitingTimes);

    return (
      <>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            borderBottomStyle: "solid",
            borderBottomColor: "divider",
            borderWidth: 1,
            alignItems: "center",
            padding: "12px",
            cursor: "pointer",

            backgroundColor:
              appointment.group_id === hoverOverId ||
              appointment.reservation_id === hoverOverId
                ? alpha(theme.palette.primary.main, 0.2)
                : appointment.status_id === status.CONFIRMED
                  ? "#FDE0CE"
                  : "background.paper",
          }}
          onClick={clickHandler}
          onMouseEnter={() =>
            handleMouseEnter(appointment.group_id ?? appointment.reservation_id)
          }
          onMouseLeave={handleMouseLeave}
          data-test-id="checked-in-client"
          data-test-email={appointment?.client?.email}
        >
          <Box sx={{ flex: "0 0 60%", minWidth: "0" }}>
            <ClientName appointment={appointment} nameDayNames={nameDayNames} />
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: "0 0 40%",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              fontWeight: "bold",
            }}
          >
            <Box sx={{ flex: "0 0 30%", color: "grey" }}>
              {parentAppointment
                ? f.time24(parentAppointment.start)
                : f.time24(appointment.start)}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {timesIcons?.checked_in_minutes}
              {timesIcons?.waiting_minutes}
            </Box>
          </Box>
          {appointment.group_id === hoverOverId && (
            <Box
              component="span"
              sx={{
                position: "absolute",
                left: "50%",
                bottom: "5px",
                color: "primary.dark",
              }}
            >
              <FontAwesomeIcon icon={faLinkHorizontal} />
            </Box>
          )}
        </Box>
      </>
    );
  },
);

CheckedInClient.displayName = "CheckedInClient";
